import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { CornerDownRight, Loader2, Send, Trash2 } from 'lucide-react';
import { comunidadService } from '../../services/comunidadService';
import { useUIStore } from '../../store/uiStore';
import { Comentario, mensajeDeError } from '../../types/comunidad';
import InsigniaRol from './InsigniaRol';
import { tiempoRelativo } from './tiempoRelativo';

interface HiloPostProps {
  postId: number;
  /** Id de quien esta mirando: define que respuestas puede borrar. */
  usuarioId: number | undefined;
  /** PSICOLOGO/ADMIN: pueden quitar cualquier respuesta. */
  esModerador: boolean;
  /**
   * Avisa a la tarjeta con el TOTAL real de respuestas, no con un delta:
   * al abrir el hilo, el numero de la tarjeta queda contrastado con el
   * del servidor en vez de acumular suposiciones.
   */
  onTotalCambia?: (total: number) => void;
}

const MAX = 500;

/**
 * Hilo de respuestas de un post: leer, responder, responder a una
 * respuesta y borrar.
 *
 * POR QUÉ UN DESPLEGABLE DENTRO DE LA TARJETA
 * -------------------------------------------
 * El boton "Ver comentarios" no tenia `onClick`: el hilo existia en el
 * backend y en el service, pero no en pantalla. Ahora la tarjeta lo
 * despliega bajo si misma en vez de llevar a otra pagina: responder es
 * parte de leer el post, y saltar de pantalla pierde el contexto de a que
 * se responde.
 *
 * El hilo se pide al montar (el post se abrio para eso), no con la lista:
 * traer todas las respuestas de los 20 posts de la pantalla haria lento
 * el listado por algo que casi nadie mira.
 *
 * CLASES: nada de opacidades sobre tokens `v-*` (`bg-v-error/10` y
 * compañía). Los tokens son hex y Tailwind no compone alfa con ellos: la
 * clase no se emite y el efecto desaparece en silencio. Para un tinte
 * sutil se usa `v-background`, que si es un color real.
 */
export default function HiloPost({ postId, usuarioId, esModerador, onTotalCambia }: HiloPostProps) {
  const { addToast } = useUIStore();
  /** `null` = cargando. Distinguirlo de `[]` evita que el "todavia no hay
   *  respuestas" parpadee encima del spinner. */
  const [comentarios, setComentarios] = useState<Comentario[] | null>(null);
  const [borrando, setBorrando] = useState<number | null>(null);

  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);

  const [respondiendoA, setRespondiendoA] = useState<number | null>(null);
  const [textoRespuesta, setTextoRespuesta] = useState('');
  const [enviandoRespuesta, setEnviandoRespuesta] = useState(false);

  const areaPrincipal = useRef<HTMLTextAreaElement>(null);
  // Una sola ref para todos los formularios de respuesta: solo hay una
  // respuesta abierta a la vez. Declararla dentro del `.map` seria llamar
  // a un hook en bucle.
  const areaRespuesta = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    let vigente = true;

    (async () => {
      try {
        const post = await comunidadService.getPost(postId);
        if (!vigente) return;
        const hilo = post.comentarios ?? [];
        setComentarios(hilo);
        onTotalCambia?.(hilo.length);
      } catch (error) {
        if (!vigente) return;
        setComentarios([]);
        addToast({
          type: 'error',
          title: 'No se pudieron cargar las respuestas',
          message: mensajeDeError(error, 'Intenta de nuevo en un momento'),
        });
      }
    })();

    return () => {
      vigente = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postId]);

  const puedeBorrar = useCallback(
    (c: Comentario) => c.usuarioId === usuarioId || esModerador,
    [usuarioId, esModerador],
  );

  // Al abrir el formulario de respuesta el foco va a el: en movil eso
  // levanta el teclado justo donde se escribe y no en el post de arriba,
  // que es donde dejaste el dedo.
  useEffect(() => {
    if (respondiendoA !== null) areaRespuesta.current?.focus();
  }, [respondiendoA]);

  const { raices, respuestasDe } = useMemo(() => {
    const lista = comentarios ?? [];
    return {
      raices: lista.filter((c) => !c.parentId),
      respuestasDe: (id: number) => lista.filter((c) => c.parentId === id),
    };
  }, [comentarios]);

  /**
   * Publica y anade al hilo sin recargar: el backend devuelve el
   * comentario CON su autor, asi que se pinta con nombre e insignia sin
   * otra peticion.
   */
  const publicar = async (parentId?: number) => {
    const contenido = (parentId ? textoRespuesta : texto).trim();
    if (!contenido) return;
    if (parentId ? enviandoRespuesta : enviando) return;

    if (parentId) setEnviandoRespuesta(true);
    else setEnviando(true);

    try {
      const nuevo = await comunidadService.createComentario({ postId, contenido, parentId });
      const siguiente = [...(comentarios ?? []), nuevo];
      setComentarios(siguiente);
      onTotalCambia?.(siguiente.length);

      if (parentId) {
        setTextoRespuesta('');
        setRespondiendoA(null);
      } else {
        setTexto('');
        areaPrincipal.current?.focus();
      }
    } catch (error) {
      addToast({
        type: 'error',
        title: 'No se pudo publicar',
        message: mensajeDeError(error, 'No se pudo enviar tu respuesta'),
      });
    } finally {
      if (parentId) setEnviandoRespuesta(false);
      else setEnviando(false);
    }
  };

  const eliminar = async (id: number) => {
    setBorrando(id);
    try {
      await comunidadService.deleteComentario(id);
      const siguiente = (comentarios ?? []).filter((c) => c.id !== id);
      setComentarios(siguiente);
      onTotalCambia?.(siguiente.length);
    } catch (error) {
      addToast({
        type: 'error',
        title: 'No se pudo borrar',
        message: mensajeDeError(error, 'No se pudo borrar la respuesta'),
      });
    } finally {
      setBorrando(null);
    }
  };

  const total = comentarios?.length ?? 0;

  return (
    <div className="mt-4 border-t border-v-outline pt-4">
      <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-v-on-surface">
        Respuestas
        {comentarios !== null && (
          <span className="rounded-full bg-v-background px-2 py-0.5 text-xs font-normal text-v-on-surface-variant">
            {total}
          </span>
        )}
      </h4>

      {comentarios === null ? (
        <div className="flex items-center gap-2 py-4 text-sm text-v-on-surface-variant">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Cargando respuestas…</span>
        </div>
      ) : total === 0 ? (
        <p className="rounded-lg bg-v-background px-3 py-4 text-center text-sm text-v-on-surface-variant">
          Todavía no hay respuestas. Sé la primera persona en responder.
        </p>
      ) : (
        <ul className="space-y-4">
          {raices.map((c) => (
            <li key={c.id}>
              <Respuesta
                comentario={c}
                puedeBorrar={puedeBorrar(c)}
                borrando={borrando === c.id}
                onResponder={() => setRespondiendoA(c.id)}
                onEliminar={() => eliminar(c.id)}
              />

              {respuestasDe(c.id).map((r) => (
                <div key={r.id} className="ml-3 mt-3 border-l-2 border-v-outline pl-3 md:ml-6">
                  <Respuesta
                    comentario={r}
                    puedeBorrar={puedeBorrar(r)}
                    borrando={borrando === r.id}
                    enLinea
                    onEliminar={() => eliminar(r.id)}
                  />
                </div>
              ))}

              {respondiendoA === c.id && (
                <div className="ml-3 mt-3 border-l-2 border-v-primary pl-3 md:ml-6">
                  <p className="mb-2 flex items-center gap-1 text-xs text-v-on-surface-variant">
                    <CornerDownRight className="h-3 w-3" />
                    Respondiendo a {c.usuario?.nombre ?? 'esta respuesta'}
                  </p>
                  <Composer
                    areaRef={areaRespuesta}
                    valor={textoRespuesta}
                    onChange={setTextoRespuesta}
                    onEnviar={() => publicar(c.id)}
                    onCancelar={() => {
                      setRespondiendoA(null);
                      setTextoRespuesta('');
                    }}
                    enviando={enviandoRespuesta}
                    etiqueta="Escribe tu respuesta"
                    boton="Responder"
                  />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="mt-4 border-t border-v-outline pt-4">
        <Composer
          areaRef={areaPrincipal}
          valor={texto}
          onChange={setTexto}
          onEnviar={() => publicar()}
          enviando={enviando}
          etiqueta="Escribe una respuesta"
          boton="Publicar respuesta"
        />
      </div>
    </div>
  );
}

interface RespuestaProps {
  comentario: Comentario;
  puedeBorrar: boolean;
  borrando: boolean;
  enLinea?: boolean;
  /** Solo en las de primer nivel: las respuestas a una respuesta no se
   *  anidan mas (un nivel basta para un hilo legible en movil). */
  onResponder?: () => void;
  onEliminar: () => void;
}

function Respuesta({
  comentario,
  puedeBorrar,
  borrando,
  enLinea = false,
  onResponder,
  onEliminar,
}: RespuestaProps) {
  const autor = comentario.usuario?.nombre ?? 'Participante';
  const inicial = autor.charAt(0).toUpperCase();

  return (
    <div className={enLinea ? 'flex gap-2' : 'flex gap-3'}>
      <span
        aria-hidden
        className={`grid shrink-0 place-items-center rounded-full bg-v-primary font-semibold text-white ${
          enLinea ? 'h-6 w-6 text-[10px]' : 'h-8 w-8 text-xs'
        }`}
      >
        {inicial}
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-sm font-semibold text-v-on-surface">{autor}</span>
          <InsigniaRol rol={comentario.usuario?.rol} />
          <span className="text-xs text-v-on-surface-variant">{tiempoRelativo(comentario.fecha)}</span>
        </div>

        <p className="mt-1 whitespace-pre-wrap break-words text-sm text-v-on-surface">
          {comentario.contenido}
        </p>

        <div className="mt-1 flex items-center gap-3">
          {!enLinea && (
            <button
              type="button"
              onClick={onResponder}
              className="no-tap-highlight -ml-2 flex min-h-11 items-center gap-1 rounded-lg px-2 text-sm font-medium text-v-on-surface-variant"
            >
              <CornerDownRight className="h-4 w-4" />
              Responder
            </button>
          )}
          {puedeBorrar && (
            <button
              type="button"
              onClick={onEliminar}
              disabled={borrando}
              aria-label={`Borrar la respuesta de ${autor}`}
              className="no-tap-highlight flex min-h-11 items-center gap-1 rounded-lg px-2 text-sm font-medium text-v-on-surface-variant disabled:opacity-60"
            >
              {borrando ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Trash2 className="h-3 w-3" />
              )}
              Borrar
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

interface ComposerProps {
  areaRef: React.RefObject<HTMLTextAreaElement>;
  valor: string;
  onChange: (valor: string) => void;
  onEnviar: () => void;
  onCancelar?: () => void;
  enviando: boolean;
  etiqueta: string;
  boton: string;
}

function Composer({
  areaRef,
  valor,
  onChange,
  onEnviar,
  onCancelar,
  enviando,
  etiqueta,
  boton,
}: ComposerProps) {
  const id = useId();
  const vacio = valor.trim().length === 0;

  return (
    <div>
      <label className="sr-only" htmlFor={id}>
        {etiqueta}
      </label>
      <textarea
        id={id}
        ref={areaRef}
        value={valor}
        onChange={(e) => onChange(e.target.value)}
        rows={2}
        maxLength={MAX}
        placeholder={etiqueta}
        className="w-full resize-none rounded-lg border border-v-outline bg-v-background px-3 py-2 text-sm text-v-on-surface placeholder:text-v-on-surface-variant focus:border-v-primary focus:outline-none focus:ring-2 focus:ring-primary"
      />

      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="text-xs text-v-on-surface-variant">
          {valor.length}/{MAX}
        </span>

        <div className="flex items-center gap-2">
          {onCancelar && (
            <button
              type="button"
              onClick={onCancelar}
              className="rounded-lg px-3 py-1.5 text-sm text-v-on-surface-variant transition-colors hover:bg-black/5"
            >
              Cancelar
            </button>
          )}
          <button
            type="button"
            onClick={onEnviar}
            disabled={vacio || enviando}
            className="flex items-center gap-1.5 rounded-lg bg-v-primary px-3 py-1.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {enviando ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            {enviando ? 'Enviando…' : boton}
          </button>
        </div>
      </div>
    </div>
  );
}
